import { useMemo, useState, type ReactNode } from "react";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Value to sort by; omit to make the column unsortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  /** Hidden below this width, so phones see the essentials. */
  hideBelow?: "sm" | "md" | "lg";
  width?: string;
}

interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  empty: ReactNode;
  loading?: boolean;
  /** Accessible label for the row action, e.g. "Open Amara Okafor". */
  rowLabel?: (row: T) => string;
}

const HIDE: Record<NonNullable<Column<unknown>["hideBelow"]>, string> = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
};

/**
 * The admin data table: sticky header, click-to-sort columns (sorts the page in view; filters narrow the set),
 * zebra-free rows with a hover highlight, the whole row opens the record, and low-priority columns drop off on
 * narrow screens instead of forcing sideways scrolling.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedKey,
  empty,
  loading,
  rowLabel,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const sorted = useMemo(() => {
    const column = sort && columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    return [...rows].sort((a, b) => {
      const x = column.sortValue!(a);
      const y = column.sortValue!(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (x < y ? -1 : x > y ? 1 : 0) * sort!.dir;
    });
  }, [rows, columns, sort]);

  const align = (a?: Column<T>["align"]) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className="overflow-hidden rounded-[14px] border border-border bg-surface shadow-[0_1px_2px_rgba(35,20,22,0.04)]">
      <div className="max-h-[calc(100dvh-300px)] min-h-[240px] overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-[13.5px]">
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={active ? (sort!.dir === 1 ? "ascending" : "descending") : undefined}
                    className={`sticky top-0 z-10 border-b border-border bg-[#fbf6ee] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.07em] text-text-muted ${align(column.align)} ${column.hideBelow ? HIDE[column.hideBelow] : ""}`}
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSort(
                            active
                              ? sort!.dir === 1
                                ? { key: column.key, dir: -1 }
                                : null
                              : { key: column.key, dir: 1 },
                          )
                        }
                        className={`inline-flex items-center gap-1 uppercase hover:text-text ${active ? "text-accent" : ""}`}
                      >
                        {column.header}
                        <span aria-hidden="true" className="text-[10px]">
                          {active ? (sort!.dir === 1 ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {onRowClick ? (
                <th
                  aria-hidden="true"
                  className="sticky top-0 z-10 w-10 border-b border-border bg-[#fbf6ee]"
                />
              ) : null}
            </tr>
          </thead>
          <tbody className={loading ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {sorted.map((row) => {
              const key = rowKey(row);
              const selected = selectedKey === key;
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`group ${onRowClick ? "cursor-pointer" : ""} ${selected ? "bg-accent-tint" : "hover:bg-[#fcf8f2]"}`}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`border-b border-[#efe3d2] px-4 py-3 align-middle ${align(column.align)} ${column.hideBelow ? HIDE[column.hideBelow] : ""}`}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                  {onRowClick ? (
                    <td className="border-b border-[#efe3d2] pr-3 text-right">
                      <button
                        type="button"
                        aria-label={rowLabel ? rowLabel(row) : "Open"}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRowClick(row);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-text-muted group-hover:bg-surface-high group-hover:text-accent"
                      >
                        ›
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!sorted.length && !loading ? (
          <div className="px-6 py-14 text-center text-[14px] text-text-muted">{empty}</div>
        ) : null}
      </div>
    </div>
  );
}
